import { useMemo } from 'react'
import type { TFunction } from 'i18next'
import {
  useEndpointMutations,
  useEndpoints,
  useQuestionTemplates,
  useRelationshipMutations,
  useRelationships,
  useSelection,
} from '../../../hooks'
import { useCollectionSet } from '../../../hooks/use-collection-set'
import { useMapSet } from '../../../hooks/use-map-set'
import type { CaseEndpoint, EndpointRelationship } from '../../../types/case-container'
import { getFilterDefinitions } from './case-infrastructure-tab.helpers'
import {
  useCaseInfrastructureSearch,
  type SearchFilterState,
} from './use-case-infrastructure-search'
import {
  useCaseInfrastructureColumns,
  type ColumnVisibilityState,
} from './use-case-infrastructure-columns'
import { useCaseInfrastructureModals, type ModalsState } from './use-case-infrastructure-modals'
import { useCaseInfrastructureActions, type ActionsState } from './use-case-infrastructure-actions'
import {
  useCaseInfrastructureHandlers,
  type CaseInfrastructureHandlers,
} from './use-case-infrastructure-handlers'

interface EndpointDerivations {
  existingCustomRoles: string[]
  getRelationshipCount: (endpointId: string) => number
}

function useEndpointDerivations(
  endpoints: CaseEndpoint[],
  relationships: EndpointRelationship[]
): EndpointDerivations {
  const existingCustomRoles = useMemo(() => {
    const allRoles: string[] = []
    endpoints.forEach((ep) => {
      if (ep.roles.length > 0) allRoles.push(...ep.roles)
    })
    return [...new Set(allRoles)]
  }, [endpoints])

  const getRelationshipCount = (endpointId: string): number =>
    relationships.filter(
      (rel) => rel.sourceEndpointId === endpointId || rel.targetEndpointId === endpointId
    ).length

  return { existingCustomRoles, getRelationshipCount }
}

export interface CaseInfrastructureTabState {
  caseId: string
  endpoints: CaseEndpoint[]
  relationships: EndpointRelationship[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  endpointMut: ReturnType<typeof useEndpointMutations>
  relMut: ReturnType<typeof useRelationshipMutations>
  sel: ReturnType<typeof useSelection>
  mapSet: ReturnType<typeof useMapSet>
  collectionSet: ReturnType<typeof useCollectionSet>
  questionTemplates: ReturnType<typeof useQuestionTemplates>['templates']
  existingCustomRoles: string[]
  getRelationshipCount: (endpointId: string) => number
  search: SearchFilterState
  columns: ColumnVisibilityState
  modals: ModalsState
  actions: ActionsState
  handlers: CaseInfrastructureHandlers
}

interface BuildHandlersArgsInput {
  caseId: string
  modals: ModalsState
  endpointMut: ReturnType<typeof useEndpointMutations>
  relMut: ReturnType<typeof useRelationshipMutations>
  refetch: () => unknown
  refetchRelationships: () => unknown
}

function buildHandlersArgs({
  caseId,
  modals,
  endpointMut,
  relMut,
  refetch,
  refetchRelationships,
}: BuildHandlersArgsInput): Parameters<typeof useCaseInfrastructureHandlers>[0] {
  return {
    caseId,
    editingEndpoint: modals.editingEndpoint,
    editingRelationship: modals.editingRelationship,
    copyLinksTargetEndpoint: modals.copyLinksTargetEndpoint,
    setShowCreateModal: modals.setShowCreateModal,
    setEditingEndpoint: modals.setEditingEndpoint,
    setLinkingEndpoint: modals.setLinkingEndpoint,
    setEditingRelationship: modals.setEditingRelationship,
    setBulkLinkSourceEndpoint: modals.setBulkLinkSourceEndpoint,
    setCopyingLinks: modals.setCopyingLinks,
    setCopyError: modals.setCopyError,
    setMovingRelationship: modals.setMovingRelationship,
    create: endpointMut.create,
    update: endpointMut.update,
    createRelationship: relMut.create,
    removeRelationship: relMut.remove,
    importBulk: endpointMut.importBulk,
    refetch,
    refetchRelationships,
  }
}

export function useCaseInfrastructureTab(args: {
  caseId: string
  t: TFunction<'cases'>
}): CaseInfrastructureTabState {
  const { caseId, t } = args
  const FILTER_DEFINITIONS = useMemo(() => getFilterDefinitions(t), [t])

  const search = useCaseInfrastructureSearch(FILTER_DEFINITIONS)
  const columns = useCaseInfrastructureColumns({ caseId, t })
  const modals = useCaseInfrastructureModals()

  const { endpoints, loading, error, refetch } = useEndpoints(caseId, search.queryParams)
  const { relationships, refetch: refetchRelationships } = useRelationships(caseId)
  const endpointMut = useEndpointMutations()
  const relMut = useRelationshipMutations()
  const sel = useSelection(caseId)
  const mapSet = useMapSet(caseId)
  const collectionSet = useCollectionSet()
  const { templates: questionTemplates } = useQuestionTemplates()

  const { existingCustomRoles, getRelationshipCount } = useEndpointDerivations(
    endpoints,
    relationships
  )

  const actions = useCaseInfrastructureActions({
    caseId,
    endpoints,
    selectedIds: sel.selectedIds,
    selectMany: sel.selectMany,
    toggle: sel.toggle,
    deselect: sel.deselect,
    remove: endpointMut.remove,
    refetch,
    addManyToMapSet: mapSet.addMany,
    addManyToCollectionSet: collectionSet.addMultiple,
    isInCollectionSet: collectionSet.isInSet,
    addToCollectionSet: collectionSet.addToSet,
    removeFromCollectionSet: collectionSet.removeFromSet,
    modals,
  })

  const handlers = useCaseInfrastructureHandlers(
    buildHandlersArgs({ caseId, modals, endpointMut, relMut, refetch, refetchRelationships }),
  )

  return {
    caseId,
    endpoints,
    relationships,
    loading,
    error,
    refetch,
    endpointMut,
    relMut,
    sel,
    mapSet,
    collectionSet,
    questionTemplates,
    existingCustomRoles,
    getRelationshipCount,
    search,
    columns,
    modals,
    actions,
    handlers,
  }
}
